import { BudgetTransaction } from 'types/BudgetTransaction';
import type { GroupTransaction } from 'types/GroupTransaction';
import type { UserEntry } from 'types/Group';
import { ApiErrorResponse, ApiResponse, isApiError } from 'types/ApiResponse';
import { apiGet, apiPost, apiPut, apiDelete } from 'utils/apiClient';
import { invalidateProfileStatsCache } from 'lib/profileStatsCache';
import { toMLLanguage } from 'constants/mlApi';

export interface AllBudgetTransactionsResponse {
    budgetTransactions: BudgetTransaction[];
    groupTransactions: GroupTransaction[];
    groupPeople?: UserEntry[];
}

// -----------------------------------------------------------------------------
//   Service calls
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
async function addTransaction(
    username: string,
    transaction: BudgetTransaction,
    language?: string,
): Promise<ApiResponse<BudgetTransaction>>
{
    const response = await apiPost<ApiResponse<BudgetTransaction>>(
        `/budget/${username}/transactions`,
        {
            Transaction: transaction,
            language: language ? toMLLanguage(language) : undefined,
        }
    );
    invalidateProfileStatsCache(username);
    return assertBudgetResponse(response);
}

// -----------------------------------------------------------------------------
async function updateTransaction(
    username: string,
    transaction: BudgetTransaction,
    language?: string,
    token?: string,
): Promise<ApiResponse<BudgetTransaction>>
{
    const response = await apiPut<ApiResponse<BudgetTransaction>>(
        `/budget/${username}/transactions`,
        {
            Transaction: transaction,
            language: language ? toMLLanguage(language) : undefined,
        },
        token
    );
    invalidateProfileStatsCache(username);
    return assertBudgetResponse(response);
}

// -----------------------------------------------------------------------------
async function deleteTransaction(username: string, transaction: BudgetTransaction, token?: string): Promise<ApiResponse<{ success: boolean }>>
{
    const response = await apiDelete<ApiResponse<{ success: boolean }>>(
        `/budget/${username}/transactions`,
        { Transaction: transaction },
        token
    );
    invalidateProfileStatsCache(username);
    return assertBudgetResponse(response);
}

export interface MarkImportStatementPaidResponse {
    importId: string;
    isPaid: boolean;
    updatedTransactions: BudgetTransaction[];
    paymentTransactionIds?: string[];
    removedPaymentTransactionIds?: string[];
}

// -----------------------------------------------------------------------------
async function markImportStatementPaid(
    username: string,
    payload: {
        importId: string;
        poolByCurrency: Record<string, string | undefined>;
        paidAt?: string;
    },
    token?: string,
): Promise<MarkImportStatementPaidResponse> {
    const response = await apiPut<MarkImportStatementPaidResponse | ApiErrorResponse>(
        `/budget/${username}/transactions/mark-import-paid`,
        payload,
        token,
    );
    if (!response || typeof response !== "object") {
        throw new Error("Failed to mark statement paid");
    }
    if ("error" in response) {
        throw new Error(response.error || response.message || "Failed to mark statement paid");
    }
    invalidateProfileStatsCache(username);
    return response as MarkImportStatementPaidResponse;
}

// -----------------------------------------------------------------------------
async function getAllTransactionsForUser(username: string, token: string): Promise<AllBudgetTransactionsResponse> {
    const response = await apiGet<AllBudgetTransactionsResponse>(`/budget/${username}/transactions`, token);
    if (!response || typeof response !== "object" || Array.isArray(response)) {
        throw new Error("Invalid transactions response payload");
    }

    return {
        budgetTransactions: response.budgetTransactions ?? [],
        groupTransactions: response.groupTransactions ?? [],
        groupPeople: response.groupPeople ?? [],
    };
}

function assertBudgetResponse<T>(response: ApiResponse<T>): ApiResponse<T> {
    if (isApiError(response)) {
        throw new Error(response.error || response.message || "Budget request failed");
    }
    return response;
}

export {
    addTransaction,
    updateTransaction,
    deleteTransaction,
    getAllTransactionsForUser,
    markImportStatementPaid,
}
